import {
  registerParamateInput,
  unregisterParamateInput,
  markParamPending,
  clearParamPending,
  liveAppData,
  type ParamateLinkedInput
} from '../../store/store.ts';
import { isProduction } from '../../data/constants.ts';
import { currentProjectData } from '../../data/projectManagement.ts';
import './as-param-warning.ts';
import { HistoryTrackingMixin } from './history-tracking-mixin.ts';

export type InternalSetValueOptions = {
  emitInput?: boolean;
  emitChange?: boolean;
  reflectAttr?: boolean;
  commit?: boolean;
};

export type SyncMode = 'auto' | 'manual';

export type TrCadInputType = undefined|'string'|'int'|'float'|'bool'|'color'|'matrix'|'vector2d'|'vector3d'|'vector'|'matrix'|'curve'|'sketch'

// This interface acts as your "source of truth" for type mapping
export interface TrCadTypeMap {
  undefined: unknown;
  'string': string;
  'int': number;
  'float': number;
  'bool': boolean;
  'color': string;
  'matrix': number[];
  'vector2d': [number, number];
  'vector3d': [number, number, number];
  'vector': number[];
  'curve': any; // Define specific types as needed
  'sketch': any;
}
/**
 * Base class for input components with shared Paramate integration.
 * Works with both Light DOM and Shadow DOM implementations.
 *
 * Attributes:
 * - trcad-param: Parameter name for Paramate integration
 * - sync-mode: 'auto' (default) or 'manual'
 *   - auto: Syncs with Paramate automatically on value changes
 *   - manual: Requires explicit sync via as-param-needs-sync event (TODO)
 * - debounce-ms: Debounce delay in ms for Paramate communication (default: 150)
 * - no-debounce: Disables debounce entirely (sets delay to 0)
 *
 * Subclasses must implement:
 * - _getValue(): returns the current value
 * - _applyValue(val, options): sets the value internally
 * - _convertFromParamate(val): converts Paramate value to component's type
 * - _getInputElement(): returns the input element (for disabled state)
 */
export abstract class AsInputBase<T extends TrCadInputType> extends HistoryTrackingMixin(HTMLElement) {
  static readonly DEFAULT_DEBOUNCE_MS = 150;

  public trCadType: T


  protected _value: TrCadTypeMap[T];
  get value(){
    return this._value
  }
  set value(value:any){
      if(this._checkType(value)){
        this._value = value
      }
  }

  // Paramate integration
  protected _paramName: string = '';
  protected _paramateRegistryEntry: ParamateLinkedInput | null = null;
  protected _lastConfirmedValue: any = null;
  protected _configurator: any = null;
  protected _syncMode: SyncMode = 'auto';
  protected _initialValueAttr: string | null = null;

  protected _isParamMissing: boolean = false;
  public get isParamMissing(): boolean {
    return this._isParamMissing && !isProduction;
  }

  // Configure-on-change: when true, setParamValue triggers immediate configure()
  protected _configureOnChange: boolean = false;

  // Ignore-history: when true, value changes are not pushed to the undo stack
  protected _ignoreHistory: boolean = false;

  // Debounce
  protected _debounceMs: number = AsInputBase.DEFAULT_DEBOUNCE_MS;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Base-class attribute observer (trcad-param, sync-mode, debounce-ms, no-debounce, cooloff)
  private _baseAttrObserver: MutationObserver | null = null;
  private _boundRestoreInputValues: ((event: Event) => void) | null = null;
  private _boundRestoreParamValues: ((event: Event) => void) | null = null;
  private _boundProjectNew: ((event: Event) => void) | null = null;
  private _boundCustomDataChanged: ((event: Event) => void) | null = null;

  // Cooloff/throttle for event emission
  protected _cooloffMs: number = 0;
  private _cooloffInputState = { inCooloff: false, timer: null as ReturnType<typeof setTimeout> | null, queuedFn: null as (() => void) | null };
  private _cooloffChangeState = { inCooloff: false, timer: null as ReturnType<typeof setTimeout> | null, queuedFn: null as (() => void) | null };

  public isArrayType = false


  // ============================================================================
  // Abstract methods - subclasses must implement
  // ============================================================================

  /** Get the current value */
  protected abstract _getValue(): TrCadTypeMap[T];

  protected _getHistoryValue(): any { return this._getValue(); }
  
  /** Apply value internally with options */
  protected abstract _applyValue(
      val: TrCadTypeMap[T], 
      options: InternalSetValueOptions
    ): void;


  


  /** Convert Paramate value to component's native type (e.g., Boolean, String, Number) */
  protected abstract _convertFromParamate(val: any): any;

  /** Get the input element for disabled state handling */
  protected abstract _getInputElement(): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement | null;

  /** 
   * Hook called when customData changes. Subclasses can override this to
   * react to application-level state changes.
   */
  protected onCustomDataUpdate(_customData: Record<string, any>): void { /* optional hook */ }

  /** 
   * Reset the input to its default value. 
   * Subclasses should override this to provide specific reset logic.
   * Default implementation uses the initial value attribute.
   */
  public resetToDefault(opt?: { silenceChange?: boolean }): void {
    const silenceChange = opt?.silenceChange ?? true; // Default to silent
    const val = this._initialValueAttr;
    if (val !== null) {
      const converted = this._convertFromParamate(val);
      this._applyValue(converted, {
        emitInput: false,
        emitChange: !silenceChange,
        reflectAttr: true,
        commit: true,
      });
      this._lastConfirmedValue = this._getValue();
    }
  }

  private _handleProjectNew = (): void => {
    this.resetToDefault({ silenceChange: true });
  };

  // ============================================================================
  // Paramate Registration (call from subclass lifecycle methods)
  // ============================================================================

  /** Call from connectedCallback after rendering */
  protected _registerParamate(): void {
    // Capture initial value for resets
    this._initialValueAttr = this.getAttribute('value');

    // Read initial attribute values
    this._syncMode = this.getAttribute('sync-mode') === 'manual' ? 'manual' : 'auto';
    this._configureOnChange = this.hasAttribute('configure-on-change');
    this._ignoreHistory = this.hasAttribute('ignore-history');
    this._readDebounceAttributes();
    this._readCooloffAttribute();

    if (this.hasAttribute('trcad-param')) {
      this._paramName = this.getAttribute('trcad-param') ?? '';
      if (this._paramName) {
        this._paramateRegistryEntry = registerParamateInput(this._paramName, this);
        this._lastConfirmedValue = this._getValue();
        this._requestConfigurator();
      }
    }

    // Single observer for all base-class attributes
    this._baseAttrObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        switch (m.attributeName) {
          case 'trcad-param':
            this._onParamAttributeChange(this.getAttribute('trcad-param'));
            break;
          case 'sync-mode':
            this._syncMode = this.getAttribute('sync-mode') === 'manual' ? 'manual' : 'auto';
            break;
          case 'configure-on-change':
            this._configureOnChange = this.hasAttribute('configure-on-change');
            break;
          case 'ignore-history':
            this._ignoreHistory = this.hasAttribute('ignore-history');
            break;
          case 'debounce-ms':
          case 'no-debounce':
            this._readDebounceAttributes();
            break;
          case 'cooloff':
            this._readCooloffAttribute();
            break;
          case 'readonly':
          case 'locked':
            this._applyReadonlyState();
            break;
        }
      }
    });
    this._baseAttrObserver.observe(this, {
      attributes: true,
      attributeFilter: ['trcad-param', 'sync-mode', 'configure-on-change', 'ignore-history', 'debounce-ms', 'no-debounce', 'cooloff', 'readonly', 'locked'],
    });

    this.addEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);

    // Listen for project restore — self-apply saved value by element ID
    this._boundRestoreInputValues = this._handleRestoreInputValues.bind(this);
    window.addEventListener('as-restore-input-values', this._boundRestoreInputValues);

    // Listen for param-keyed undo/redo restore (linked inputs only)
    this._boundRestoreParamValues = this._handleRestoreParamValues.bind(this);
    window.addEventListener('as-restore-param-values', this._boundRestoreParamValues);

    // Listen for project reset
    this._boundProjectNew = this._handleProjectNew.bind(this) as any;
    window.addEventListener('as-inputs-reset', this._boundProjectNew);

    // Listen for customData changes
    this._boundCustomDataChanged = this._handleCustomDataChanged.bind(this);
    window.addEventListener('as-custom-data-changed', this._boundCustomDataChanged);

    // Apply initial readonly state
    this._applyReadonlyState();

    this._registerHistoryTracking();
  }

  /** Call from disconnectedCallback */
  protected _unregisterParamate(): void {
    // Clean up debounce
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    // Clean up cooloff timers
    if (this._cooloffInputState.timer !== null) {
      clearTimeout(this._cooloffInputState.timer);
    }
    if (this._cooloffChangeState.timer !== null) {
      clearTimeout(this._cooloffChangeState.timer);
    }
    this._cooloffInputState = { inCooloff: false, timer: null, queuedFn: null };
    this._cooloffChangeState = { inCooloff: false, timer: null, queuedFn: null };

    if (this._baseAttrObserver) {
      this._baseAttrObserver.disconnect();
      this._baseAttrObserver = null;
    }

    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
      this._paramateRegistryEntry = null;
    }

    this.removeEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);

    if (this._boundRestoreInputValues) {
      window.removeEventListener('as-restore-input-values', this._boundRestoreInputValues);
      this._boundRestoreInputValues = null;
    }

    if (this._boundRestoreParamValues) {
      window.removeEventListener('as-restore-param-values', this._boundRestoreParamValues);
      this._boundRestoreParamValues = null;
    }

    if (this._boundProjectNew) {
      window.removeEventListener('as-inputs-reset', this._boundProjectNew);
      this._boundProjectNew = null;
    }

    if (this._boundCustomDataChanged) {
      window.removeEventListener('as-custom-data-changed', this._boundCustomDataChanged);
      this._boundCustomDataChanged = null;
    }

    this._unregisterHistoryTracking();
  }

  private _handleCustomDataChanged(event: Event): void {
    const customData = (event as CustomEvent).detail?.customData ?? currentProjectData.customData;
    this.onCustomDataUpdate(customData);
  }

  private _onParamAttributeChange(newValue: string | null): void {
    // Unregister old param if exists
    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
      this._paramateRegistryEntry = null;
    }

    this._paramName = (newValue ?? '').trim();

    // Register new param if set
    if (this._paramName) {
      this._paramateRegistryEntry = registerParamateInput(this._paramName, this);
      this._lastConfirmedValue = this._getValue();
      this._requestConfigurator();
    }
  }

  /** Reads debounce-ms and no-debounce attributes and updates _debounceMs */
  private _readDebounceAttributes(): void {
    if (this.hasAttribute('no-debounce')) {
      this._debounceMs = 0;
      return;
    }
    const debounceMsAttr = this.getAttribute('debounce-ms');
    if (debounceMsAttr !== null) {
      const parsed = parseInt(debounceMsAttr, 10);
      this._debounceMs = (Number.isFinite(parsed) && parsed >= 0) ? parsed : AsInputBase.DEFAULT_DEBOUNCE_MS;
    } else {
      this._debounceMs = AsInputBase.DEFAULT_DEBOUNCE_MS;
    }
  }

  // ============================================================================
  // Cooloff / Throttle for Event Emission
  // ============================================================================

  /** Read the cooloff attribute and update _cooloffMs */
  private _readCooloffAttribute(): void {
    const cooloffAttr = this.getAttribute('cooloff');
    if (cooloffAttr !== null) {
      const parsed = parseInt(cooloffAttr, 10);
      this._cooloffMs = (Number.isFinite(parsed) && parsed >= 0) ? parsed : 0;
    } else {
      this._cooloffMs = 0;
    }
  }

  /**
   * Throttle event emission by channel ('input' or 'change').
   * If not in cooloff, fn() runs immediately and cooloff starts.
   * If in cooloff, fn is queued (latest wins) and runs when cooloff expires.
   * When cooloffMs is 0, fn() runs immediately with no throttling.
   */
  protected _throttle(channel: 'input' | 'change', fn: () => void): void {
    if (this._cooloffMs <= 0) {
      fn();
      return;
    }
    const state = channel === 'input' ? this._cooloffInputState : this._cooloffChangeState;
    if (!state.inCooloff) {
      fn();
      this._startCooloff(channel);
    } else {
      state.queuedFn = fn;
    }
  }

  private _startCooloff(channel: 'input' | 'change'): void {
    if (this._cooloffMs <= 0) return;
    const state = channel === 'input' ? this._cooloffInputState : this._cooloffChangeState;
    state.inCooloff = true;
    if (state.timer !== null) clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      state.inCooloff = false;
      state.timer = null;
      if (state.queuedFn) {
        const queued = state.queuedFn;
        state.queuedFn = null;
        queued();
        this._startCooloff(channel);
      }
    }, this._cooloffMs);
  }

  // ============================================================================
  // Paramate Communication
  // ============================================================================
  private _validators = []
  private _validatorKeys = []
  public addValidator(fn:(value:any)=>boolean,key?:string){
    this._validators.push(fn)
    this._validatorKeys.push(key??null)
  }
  public removeValidator(id:number|string):boolean{
    let index = id as number
    if( typeof id ==="string" ){
      const foundIndex = this._validatorKeys.findIndex((value)=>value===id)
      if(foundIndex<0) return false
      if(this._validators.length < foundIndex+1) return false
      index = foundIndex
    }
    
    const splicedItems = this._validators.splice(index)
    if (splicedItems.length>0){ 
      return true
    }
    else{
      console.log(`Error removing id ${id}, spliced item:${splicedItems}, final list: ${this._validators}`)
      return false
    } 
  }

  public clearValidators(){
    this._validators = []
    this._validatorKeys = []
  }

  protected _areValuesEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => v === b[i]);
    }
    return false;
  }

  protected _validateValue(value:any){
    const foundInvalid = (this._validators.map(vFn=>vFn(value)).includes(false))
    return !foundInvalid
  }
  protected _checkType(value:any):boolean{
    return true
  }

  /** Send value to Paramate - call after emitting change event. Debounced by _debounceMs. */
  protected _sendToParamate(value: any): void {
    if (!this._configurator || !this._paramName) return;

    // Mark as pending immediately for responsive UI - only in auto mode
    if (this._syncMode === 'auto') {
      if (this._areValuesEqual(value, this._lastConfirmedValue)) {
        clearParamPending(this._paramName);
      } else {
        markParamPending(this._paramName, value);
      }
    }

    if (this._debounceMs > 0) {
      if (this._debounceTimer !== null) {
        clearTimeout(this._debounceTimer);
      }
      this._debounceTimer = setTimeout(() => {
        this._debounceTimer = null;
        this._dispatchToParamate(value);
      }, this._debounceMs);
    } else {
      this._dispatchToParamate(value);
    }
  }

  /** Flush any pending debounced Paramate send immediately */
  public flushDebounce(): void {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }

  private _dispatchToParamate(value: any): void {
    if (!this._configurator || !this._paramName) return;

    const fullParamName = this._paramName.startsWith('main:')
      ? this._paramName
      : `main:${this._paramName}`;

    this.dispatchEvent(new CustomEvent('as-input-pending', {
      bubbles: true,
      composed: true,
      detail: { paramName: this._paramName, value },
    }));

    this._configurator.setParamValue?.(fullParamName, value, this._configureOnChange);
  }

  private _requestConfigurator(): void {
    if (!this._paramName) return;

    const handleConfiguratorResponse = (event: CustomEvent<{ configurator: any }>) => {
      this._configurator = event.detail.configurator;
      this.removeEventListener('configurator', handleConfiguratorResponse as EventListener);

      // Sync initial value from configurator
      this._syncInitialValue();
    };

    this.addEventListener('configurator', handleConfiguratorResponse as EventListener);

    window.dispatchEvent(new CustomEvent('get-configurator', {
      detail: { requester: this }
    }));
  }

  private _syncInitialValue(): void {
    if (!this._configurator?.params || !this._paramName) return;

    // Try to find the param - check with prefix, then without
    const fullName = this._paramName.startsWith('main:') ? this._paramName : `main:${this._paramName}`;
    const shortName = this._paramName.startsWith('main:') ? this._paramName.slice(5) : this._paramName;

    const paramData = this._configurator.params[fullName] ?? this._configurator.params[shortName];
    if (paramData !== undefined) {
      this._isParamMissing = false;
      const groundTruthValue = paramData.value ?? paramData.default;
      const convertedValue = this._convertFromParamate(groundTruthValue);

      if (convertedValue !== undefined && convertedValue !== this._getValue()) {
        this._applyValue(convertedValue, {
          emitInput: false,
          emitChange: false,
          reflectAttr: true,
          commit: true,
        });
        this._lastConfirmedValue = this._getValue();
      }

      // Apply min/max from param data if this input type supports them
      const observed = (this.constructor as any).observedAttributes as string[] | undefined;
      if (observed) {
        // Param-driven bounds go to the `auto-*` attributes so author-set `min`/`max`
        // are never clobbered and the bounds keep updating; the element combines the two
        // by taking the most restrictive.
        // See the "Paramate Integration" subtopic in appSrc/tutorial/src/topics/input-base.ts.
        if (paramData.min !== undefined && observed.includes('auto-min')) {
          this.setAttribute('auto-min', String(paramData.min));
        }
        if (paramData.max !== undefined && observed.includes('auto-max')) {
          this.setAttribute('auto-max', String(paramData.max));
        }
        if (paramData.type !== undefined && observed.includes('type') && !this.hasAttribute('type')) {
          this.setAttribute('type', String(paramData.type));
        }
        if (paramData.step !== undefined && observed.includes('step') && !this.hasAttribute('step')) {
          this.setAttribute('step', String(paramData.step));
        }
      }
    } else {
      this._isParamMissing = true;
      this.dispatchEvent(new CustomEvent('as-input-param-missing', {
        bubbles: true,
        composed: true,
        detail: { paramName: this._paramName }
      }));
      
      // Trigger visual update for warning icon
      if (typeof (this as any).updateDisplay === 'function') {
        (this as any).updateDisplay();
      } else if (typeof (this as any)._updateHelpHover === 'function') {
        (this as any)._updateHelpHover();
      }
    }
  }

  private _handleRestoreInputValues(event: Event): void {
    const { inputs, silent } = (event as CustomEvent<{ inputs: Record<string, any>; silent?: boolean }>).detail || {};
    if (!inputs || typeof inputs !== 'object') return;
    const id = this.id;
    if (!id || !(id in inputs)) return;
    if ('setValue' in this) {
      // For param-linked inputs, we DON'T silence events so they sync back to Paramate,
      // UNLESS the event explicitly requests silence (e.g. during project load with session blob).
      const isParam = this.hasAttribute('trcad-param');
      const silenceEvents = silent || !isParam;
      (this as any).setValue(inputs[id], { silenceEvents });

      // Always dispatch native change event for focus-on-change support
      this.dispatchEvent(new CustomEvent('change', { 
        bubbles: true,
        detail: {} 
      }));

      // Focus the inner element so the user can continue editing immediately
      setTimeout(() => {
        this._getInputElement()?.focus();
      }, 0);

      this.dispatchEvent(new CustomEvent('as-input-restored', {
        bubbles: true,
        composed: true,
        detail: { id, value: inputs[id] },
      }));
    }
  }

  private _handleRestoreParamValues(event: Event): void {
    if (!this._paramName) return;
    const { params } = (event as CustomEvent<{ params: Record<string, any> }>).detail || {};
    if (!params) return;

    const fullName = this._paramName.startsWith('main:') ? this._paramName : `main:${this._paramName}`;
    const value = fullName in params ? params[fullName]
      : this._paramName in params ? params[this._paramName]
      : undefined;
    if (value === undefined) return;

    const converted = this._convertFromParamate(value);
    this._applyValue(converted, { emitInput: false, emitChange: false, reflectAttr: true, commit: true });
    
    // Dispatch native change event so parent components (like as-step-selector) can react
    this.dispatchEvent(new CustomEvent('change', { 
      bubbles: true,
      detail: {}
    }));

    // Focus the inner element
    setTimeout(() => {
      this._getInputElement()?.focus();
    }, 0);

    // Keep the UI input store in sync
    if (this.id) {
      liveAppData.uiInputs = { ...liveAppData.uiInputs, [this.id]: converted };
    }

    // When restoring for a configure action, we ARE pending until the configure finishes.
    // If we are restoring to ground truth (e.g. undoing a staged change), clear it.
    if (this._areValuesEqual(converted, this._lastConfirmedValue)) {
      clearParamPending(this._paramName);
    } else {
      markParamPending(this._paramName, converted);
    }
  }

  private _handleParamateValueResolved = (event: CustomEvent<{ paramName: string; value: any }>): void => {
    const { paramName, value } = event.detail;
    const previousValue = this._getValue();

    // If value is undefined (error case), revert to last confirmed value
    if (value === undefined) {
      if (this._lastConfirmedValue !== null && this._lastConfirmedValue !== previousValue) {
        this._applyValue(this._lastConfirmedValue, {
          emitInput: false,
          emitChange: false,
          reflectAttr: true,
          commit: true,
        });
      }
      return;
    }

    // Update with ground truth value (silently to avoid loops)
    const convertedValue = this._convertFromParamate(value);
    if (convertedValue !== previousValue) {
      this._applyValue(convertedValue, {
        emitInput: false,
        emitChange: false,
        reflectAttr: true,
        commit: true,
      });
    }

    // Store as last confirmed value
    this._lastConfirmedValue = this._getValue();

    this.dispatchEvent(new CustomEvent('as-input-resolved', {
      bubbles: true,
      composed: true,
      detail: { paramName, value: this._lastConfirmedValue, previousValue },
    }));
  }

  // ============================================================================
  // Shared Utilities
  // ============================================================================

  /** Apply disabled state to input element */
  protected _applyDisabledState(): void {
    const input = this._getInputElement();
    if (input) {
      input.disabled = this.hasAttribute('disabled');
    }
  }

  // ============================================================================
  // Readonly & Locked State
  // ============================================================================

  get readonly(): boolean {
    return this.hasAttribute('readonly') || this.hasAttribute('locked');
  }

  set readonly(val: boolean) {
    if (val) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  get locked(): boolean {
    return this.hasAttribute('locked');
  }

  set locked(val: boolean) {
    if (val) {
      this.setAttribute('locked', '');
    } else {
      this.removeAttribute('locked');
    }
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  /** Apply readonly state. Subclasses can override for component-specific behavior. */
  protected _applyReadonlyState(): void {
    const input = this._getInputElement();
    if (input && 'readOnly' in input) {
      (input as HTMLInputElement).readOnly = this.readonly;
    }
  }
}
